import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import i18n from '@/libs/i18n'
import useCommon from '@/views/organization/useCommon'
import { hasPermission } from '@/composables/usePermission'
import { useLambda } from '@/views/habit/awsLambda'
import { oid } from '@/helpers/mongoHelpers'
import { EventBus } from '@/views/habit/EventBus.js'

export default function useResidualRiskList() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { handleError } = useCommon()
  const { updateItem, getItemsAggregate } = useLambda()
  const { client } = store.state?.userStore?.userData

  const refARRTable = ref(null)
  const analyses = ref([])
  const perPage = ref(10)
  const totalAnalyses = ref(0)
  const currentPage = ref(1)
  const searchQuery = ref('')
  const sortBy = ref('treatmentId')
  const isSortDirDesc = ref(true)
  const isLoading = ref(true)
  const collection = 'residualAnalysis'

  // Filters
  const levelFilter = ref(null) // residual level filter
  const decisionFilter = ref(null)
  const statusFilter = ref(null)

  const clearFiltersDisabled = computed(() =>
    !searchQuery.value &&
    !levelFilter.value &&
    !decisionFilter.value &&
    !statusFilter.value
  )

  // Update filters from Filters component
  const updateFilter = (data) => {
    levelFilter.value = data.levelFilter
    decisionFilter.value = data.decisionFilter
    statusFilter.value = data.statusFilter

    checkPageAndFetch()
  }

  // Table Handlers
  const tableColumns = computed(() => {
    return [
      {
        key: 'actions',
        label: i18n.t('message.tableHeader.actions'),
      },
      {
        key: 'treatmentId',
        label: 'ID Tratamiento',
        sortable: true,
      },
      {
        key: 'treatmentName',
        label: 'Nombre',
        sortable: true,
      },
      {
        key: 'inheritedP',
        label: 'P_inh',
        sortable: true,
      },
      {
        key: 'inheritedI',
        label: 'I_inh',
        sortable: true,
      },
      {
        key: 'inheritedRisk',
        label: 'Riesgo Inherente',
        sortable: true,
      },
      {
        key: 'inheritedLevel',
        label: 'Nivel Inherente',
        sortable: true,
      },
      {
        key: 'residualP',
        label: 'P_res',
        sortable: true,
      },
      {
        key: 'residualI',
        label: 'I_res',
        sortable: true,
      },
      {
        key: 'residualRisk',
        label: 'Riesgo Residual',
        sortable: true,
      },
      {
        key: 'residualLevel',
        label: 'Nivel Residual',
        sortable: true,
      },
      {
        key: 'riskReductionPct',
        label: 'Reducción (%)',
        sortable: true,
      },
      {
        key: 'decision',
        label: 'Decisión',
        sortable: true,
      },
      {
        key: 'status',
        label: 'Estado Plan',
        sortable: true,
      },
    ]
  })

  const fetchAnalyses = async () => {
    isLoading.value = true

    try {
      // Build query
      const query = {
        client_id: oid(client.$oid),
        deleted: { $ne: true }
      }

      // Apply filters
      if (levelFilter.value) {
        query.residualLevel = levelFilter.value
      }
      if (decisionFilter.value) {
        query.decision = decisionFilter.value
      }
      if (statusFilter.value) {
        query.status = statusFilter.value
      }

      // Clean null values
      Object.keys(query).forEach(key => {
        if (query[key] === null) delete query[key]
      })

      const pipeline = [
        { $match: query },
        {
          $lookup: {
            from: 'treatment',
            localField: 'treatment_id',
            foreignField: '_id',
            as: 'treatment',
            pipeline: [
              {
                $project: {
                  treatmentId: 1,
                  nombre: 1,
                  empresa_id: 1,
                  id_negocio: 1,
                }
              }
            ]
          }
        },
        {
          $addFields: {
            treatment: { $arrayElemAt: ['$treatment', 0] },
            treatmentId: { $arrayElemAt: ['$treatment.treatmentId', 0] },
            treatmentName: { $arrayElemAt: ['$treatment.nombre', 0] },
          }
        }
      ]

      // Search in treatmentId and treatmentName
      if (searchQuery.value) {
        pipeline.splice(3, 0, {
          $match: {
            $or: [
              { treatmentId: { $regex: searchQuery.value, $options: 'i' } },
              { treatmentName: { $regex: searchQuery.value, $options: 'i' } }
            ]
          }
        })
      }

      pipeline.push(
        { $sort: { treatmentId: isSortDirDesc.value ? -1 : 1 } },
        { $skip: (currentPage.value - 1) * perPage.value },
        { $limit: perPage.value }
      )

      const countPipeline = [
        { $match: query },
        {
          $lookup: {
            from: 'treatment',
            localField: 'treatment_id',
            foreignField: '_id',
            as: 'treatment',
            pipeline: [
              {
                $project: {
                  treatmentId: 1,
                  nombre: 1,
                }
              }
            ]
          }
        },
        {
          $addFields: {
            treatment: { $arrayElemAt: ['$treatment', 0] },
            treatmentId: { $arrayElemAt: ['$treatment.treatmentId', 0] },
            treatmentName: { $arrayElemAt: ['$treatment.nombre', 0] },
          }
        }
      ]

      // Add search to count pipeline
      if (searchQuery.value) {
        countPipeline.push({
          $match: {
            $or: [
              { treatmentId: { $regex: searchQuery.value, $options: 'i' } },
              { treatmentName: { $regex: searchQuery.value, $options: 'i' } }
            ]
          }
        })
      }

      countPipeline.push({ $count: 'total' })

      const result = await getItemsAggregate({ collection, pipeline })
      const countResult = await getItemsAggregate({ collection, pipeline: countPipeline })

      analyses.value = result || []
      totalAnalyses.value = countResult?.[0]?.total || 0
    } catch (error) {
      console.log(error)
      handleError({ error, defaultMessage: 'Error al cargar análisis de riesgo residual' })
    } finally {
      isLoading.value = false
    }
  }

  const deleteAnalysis = async (analysis) => {
    if (!hasPermission('privacy-residual-risk', 'delete')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      return
    }
    try {
      const query = { _id: oid(analysis._id), client_id: oid(client.$oid) }

      const payload = { deleted: true }
      const action = { $set: payload }
      await updateItem({ collection, query, action })

      showSuccessMessage('ARR eliminado correctamente')
      fetchAnalyses()
    } catch (error) {
      console.log(error)
      showErrorMessage('Error al eliminar ARR')
    }
  }

  const clearFilters = () => {
    searchQuery.value = ''
    levelFilter.value = null
    decisionFilter.value = null
    statusFilter.value = null

    // Emit event to reset filters in Filters component
    EventBus.$emit('resetFilters')

    checkPageAndFetch()
  }

  const checkPageAndFetch = () => {
    if (currentPage.value === 1) fetchAnalyses()
    else currentPage.value = 1
  }

  // Fetch all analyses for export (respecting filters, no pagination)
  const fetchAllAnalysesForExport = async () => {
    try {
      // Build query same as fetchAnalyses
      const query = {
        client_id: oid(client.$oid),
        deleted: { $ne: true }
      }

      // Apply filters
      if (levelFilter.value) {
        query.residualLevel = levelFilter.value
      }
      if (decisionFilter.value) {
        query.decision = decisionFilter.value
      }
      if (statusFilter.value) {
        query.status = statusFilter.value
      }

      // Clean null values
      Object.keys(query).forEach(key => {
        if (query[key] === null) delete query[key]
      })

      const pipeline = [
        { $match: query },
        {
          $lookup: {
            from: 'treatment',
            localField: 'treatment_id',
            foreignField: '_id',
            as: 'treatment',
            pipeline: [
              {
                $project: {
                  treatmentId: 1,
                  nombre: 1,
                  empresa_id: 1,
                  id_negocio: 1,
                }
              }
            ]
          }
        },
        {
          $lookup: {
            from: 'company',
            localField: 'treatment.empresa_id',
            foreignField: '_id',
            as: 'empresa',
            pipeline: [ { $project: { name: 1 } } ]
          }
        },
        {
          $lookup: {
            from: 'worker',
            localField: 'responsible_id',
            foreignField: '_id',
            as: 'responsible',
            pipeline: [
              {
                $project: {
                  name: 1,
                }
              }
            ]
          }
        },
        {
          $addFields: {
            treatment: { $arrayElemAt: ['$treatment', 0] },
            empresa: { $arrayElemAt: ['$empresa', 0] },
            responsible: { $arrayElemAt: ['$responsible', 0] },
            treatmentId: { $arrayElemAt: ['$treatment.treatmentId', 0] },
            treatmentName: { $arrayElemAt: ['$treatment.nombre', 0] },
          }
        }
      ]

      // Search in treatmentId and treatmentName
      if (searchQuery.value) {
        pipeline.push({
          $match: {
            $or: [
              { treatmentId: { $regex: searchQuery.value, $options: 'i' } },
              { treatmentName: { $regex: searchQuery.value, $options: 'i' } }
            ]
          }
        })
      }

      pipeline.push({ $sort: { treatmentId: -1 } })

      const result = await getItemsAggregate({ collection, pipeline })

      return result || []
    } catch (error) {
      console.log(error)
      handleError({ error, defaultMessage: 'Error al cargar análisis para exportar' })
      return []
    }
  }

  watch([currentPage], () => {
    fetchAnalyses()
  })

  watch([searchQuery, sortBy, isSortDirDesc, perPage], () => {
    checkPageAndFetch()
  })

  return {
    fetchAnalyses,
    tableColumns,
    perPage,
    currentPage,
    totalAnalyses,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refARRTable,
    analyses,
    deleteAnalysis,
    isLoading,
    clearFilters,
    clearFiltersDisabled,
    updateFilter,
    fetchAllAnalysesForExport,
  }
}
